import { z } from 'zod';
import { PieceModelStem } from './piece-models.js';
import { BuildSource, BuildStatus, PackageName, Sha256, SigningIdentity, Slug, Ulid, } from './common.js';
import { VerificationSummary } from './verification.js';
/**
 * The upload protocol (plan §6), specified once and implemented by the CLI and all three
 * editor plugins. Bytes never pass through the Worker: a Worker request body caps out around
 * 100 MB, so the artifact goes straight to R2 and the Worker only authorizes and records.
 *
 * Three calls:
 *   1. POST /v1/apps/:id/builds     -> authorize, reserve a build row, hand back an upload URL
 *   2. PUT  <uploadUrl>             -> the artifact itself, uploader -> R2
 *   3. POST /v1/builds/:id/finalize -> server HEADs the object and flips the build to `ready`
 */
/** 8 GiB. Well past any real Board build; a sanity bound, not a product limit. */
export const MAX_ARTIFACT_BYTES = 8 * 1024 * 1024 * 1024;
export const CreateBuildRequest = z.object({
    /**
     * Declared by the uploader and checked against the app's `package_name`. BoardFlight
     * trusts this declaration — see the accepted trade-off in plan §4. Nothing here reads
     * the artifact.
     */
    packageName: PackageName,
    versionName: z.string().trim().min(1).max(120),
    /** Must strictly exceed the current build on the target channel. */
    versionCode: z.number().int().min(1).max(2_100_000_000),
    size: z.number().int().min(1).max(MAX_ARTIFACT_BYTES),
    sha256: Sha256,
    /** Channel slug; the app's default channel when omitted. */
    channel: Slug.optional(),
    releaseNotes: z.string().max(20_000).optional(),
    gitSha: z.string().trim().max(64).optional(),
    /** e.g. "Unity 6000.0.23f1", "Godot 4.3.stable". Diagnostic only. */
    engineVersion: z.string().trim().max(120).optional(),
    /**
     * The Board Piece Model this build ships, as a registry stem (`chop_chop`).
     *
     * Declared, like everything else here: the Worker records the word and never opens the artifact
     * to check it. A CLI convenience may pre-fill it from a `.webapp.zip`'s `harness-config.json`
     * on the developer's own machine — that is the developer reading the developer's own file, and
     * what arrives is still a declaration.
     *
     * Deliberately **not** an enum over `PIECE_MODELS`: a developer may ship a model of their own
     * that no release has ever heard of, and refusing it would be refusing a build over a name.
     * The shape is checked, the vocabulary is not — a stem with no registry row is rendered by its
     * name. Absent means not declared, which is no intro screen and no pieces row.
     */
    pieceModel: PieceModelStem.optional(),
    /**
     * The channel slug these bytes were built for — the value the build itself was stamped with
     * (`VITE_BOARDFLIGHT_CHANNEL`, Unity's `Resources/boardflight-channel.txt`), so that the game's
     * `channel()` answers it (`docs/game-events-plan.md` §6.4).
     *
     * Declared, like everything above: nothing opens the artifact to check it. It must name the
     * channel the upload goes to — `400 channel_stamp_mismatch` otherwise, because a build made for
     * one channel and uploaded straight to another is a mistake at the moment it is cheapest to fix.
     * Absent is recorded as *not stamped*, which is what every build uploaded before this was.
     */
    builtFor: Slug.optional(),
    source: BuildSource,
});
export const CreateBuildResponse = z.object({
    buildId: Ulid,
    /**
     * Either a presigned R2 URL (production) or a Worker-hosted endpoint (local dev and
     * tests, where no R2 S3 credentials exist). Uploaders must treat it as opaque and send
     * exactly `method` + `headers`.
     */
    uploadUrl: z.string(),
    method: z.literal('PUT'),
    headers: z.record(z.string(), z.string()),
    expiresAt: z.number().int(),
});
export const Build = z.object({
    id: Ulid,
    appId: Ulid,
    channelId: Ulid,
    channelSlug: z.string(),
    /** Monotonic per app, assigned at finalize — an uploading build has none yet. */
    buildNumber: z.number().int().nullable(),
    versionName: z.string(),
    versionCode: z.number().int(),
    size: z.number().int(),
    sha256: z.string(),
    status: BuildStatus,
    releaseNotes: z.string().nullable(),
    source: BuildSource,
    gitSha: z.string().nullable(),
    engineVersion: z.string().nullable(),
    /** The declared Board Piece Model stem, or null. See `CreateBuildRequest.pieceModel`. */
    pieceModel: z.string().nullable().optional(),
    /**
     * The slug of the channel these bytes were stamped for, or null when the upload declared none
     * (`docs/game-events-plan.md` §6.4). It differs from `channelSlug` only after a promotion that
     * acknowledged it — the game on this build answers `builtFor`, not the channel it is on.
     */
    builtFor: z.string().nullable().optional(),
    /**
     * Whether BoardFlight holds the project this build was made from — a playground deploy linked to
     * it — and so can rebuild it for another channel (`promote` with `rebuild: true`).
     */
    rebuildable: z.boolean().optional(),
    /** The publish or rebuild job that produced this build, whose stored log is its account (§6.5). */
    publishJobId: z.string().nullable().optional(),
    uploadedBy: z.string().nullable(),
    uploadedByUserId: Ulid.nullable(),
    /** Set when a runner installed and launched this build on a real Board (plan §15). */
    verifiedAt: z.number().int().nullable(),
    /**
     * Whose signature these bytes carry, and which certificate (docs/feedback-plan.md §3.4).
     *
     * Both, not one: `signingIdentity` says *who* signed, and the client's pre-download check
     * needs to know *with what*, so it can compare against the installed package and say "this
     * will not install over what you have" **before** a 300 MB download rather than after it.
     * `signingCertSha256` is null for an untouched build — there is nothing to compare against
     * there, and Android's own "signatures do not match" is the right authority for a
     * developer's own key.
     */
    signingIdentity: SigningIdentity,
    signingCertSha256: z.string().nullable(),
    /** When the feedback layer was injected into these bytes. Null for an untouched build. */
    patchedAt: z.number().int().nullable(),
    /**
     * The newest hardware check, so the dashboard can show "queued" and "failed" too — not just
     * the pass that `verifiedAt` records. Developers only; a tester has no use for a runner's
     * name and should not see one.
     */
    verification: VerificationSummary.nullish(),
    expiresAt: z.number().int().nullable(),
    createdAt: z.number().int(),
    readyAt: z.number().int().nullable(),
});
/**
 * Where a browser should be sent to get one build's artifact (plan §9).
 *
 * `GET /v1/builds/:id/download` is the endpoint everything else uses, and a browser cannot use
 * it: it is authorized by a bearer header, and there is no way to put a header on a navigation.
 * So the dashboard asks for a URL instead and goes there, which is the same shape the upload
 * protocol already has in the other direction — `url` is either a presigned R2 URL (production)
 * or a Worker-hosted endpoint carrying a signed ticket (local dev and tests, where no R2 S3
 * credentials exist), and the caller must treat it as opaque.
 *
 * Short-lived by design: `expiresAt` is minutes away, because the URL is the capability. It is
 * minted per click after the same entitlement check `/download` makes, never stored, and never
 * put in front of a user as a link to keep.
 */
export const BuildDownloadUrl = z.object({
    url: z.string(),
    /** What the file will be called on disk — `<slug>-<version>-<code>.apk`, decided by the server. */
    filename: z.string(),
    expiresAt: z.number().int(),
});
export const PromoteBuildRequest = z.object({
    /** Target channel slug. */
    channel: Slug,
    /** Keep the build on its current channel as well, instead of moving it. */
    copy: z.boolean().default(true),
    /**
     * Promote a stamped build into a channel other than the one it was built for anyway
     * (`docs/game-events-plan.md` §6.4). Without it the API answers `409 channel_stamp_differs`:
     * the game on these bytes will keep telling itself it is on the channel it was built for, and
     * nothing promotes a stamped build somewhere else without somebody saying they know.
     */
    acceptChannelStamp: z.boolean().optional(),
    /**
     * Build it again for the target instead (`docs/game-events-plan.md` §6.4): a playground build is
     * rebuilt from the deploy it shipped in, stamped for the target, as a background job. The answer
     * is `202 { jobId }` rather than a build. `409 rebuild_unavailable` when BoardFlight does not hold
     * the source — every Unity and CLI build, and a playground build whose deploy is not linked.
     */
    rebuild: z.boolean().optional(),
});
/**
 * Why a build already on a channel cannot show that channel's settings (docs/feedback-plan.md §3.8).
 *
 *  - `unpatched` — the channel wants BoardFlight's in-game layer and the build has none at all.
 *  - `copied` — promoted here from another channel. The layer inside it asks about the build it was
 *    patched as, so it follows *that* channel's settings, not this one's (§3.7).
 *  - `screenshot` — a web build keeps its drawing buffer, or does not, from the moment the page
 *    starts. That is the one setting the runtime config cannot reach.
 *  - `events` — the channel collects custom game events and the build was patched before its
 *    platform's layer had the code to (`docs/game-events-plan.md` §8.1). A switch cannot add code.
 *  - `play` — the channel has achievements or leaderboards on and the build was patched before its
 *    layer had the play module (`docs/achievements-plan.md` §8.1). The same: no switch adds code.
 */
export const RebuildReason = z.enum(['unpatched', 'copied', 'screenshot', 'events', 'play']);
/**
 * What stops BoardFlight rebuilding a channel it says needs one.
 *
 *  - `platform` — an APK carries its own version code, and a rebuild under a higher declared one
 *    would install as the same version and be offered as an update for ever. Only a new upload can
 *    move it. A web build has no version of its own, so the code is BoardFlight's to choose.
 *  - `patcher` — this deployment has nowhere to patch.
 *  - `archived`, `not_on_store` — the same two refusals an upload to this channel would meet.
 *  - `source` — the developer's original bytes cannot be identified, so there is nothing honest to
 *    start from.
 */
export const RebuildBlocker = z.enum(['platform', 'patcher', 'archived', 'not_on_store', 'source']);
export const RebuildCandidate = z.object({
    id: Ulid,
    buildNumber: z.number().int().nullable(),
    versionName: z.string(),
    versionCode: z.number().int(),
    pieceModel: z.string().nullable(),
    reason: RebuildReason,
});
/** `GET /v1/apps/:appId/channels/:channelId/rebuild` — whether a channel's builds are behind it. */
export const ChannelRebuildPlan = z.object({
    /** At least one build a device would install from this channel cannot show its settings. */
    needed: z.boolean(),
    /** `needed`, and BoardFlight can do something about it. */
    possible: z.boolean(),
    blocker: RebuildBlocker.nullable(),
    /** The builds that are behind, one per piece-model sibling at most. */
    builds: z.array(RebuildCandidate),
    /** Builds on this channel being patched right now — a rebuild, or an ordinary upload. */
    inProgress: z.array(z.object({ id: Ulid, versionName: z.string(), versionCode: z.number().int() })),
});
export const BuildListQuery = z.object({
    channel: Slug.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    /** Opaque; currently the last build id of the previous page. */
    cursor: z.string().optional(),
    /**
     * Developers can ask for in-flight builds; testers only ever see `ready`.
     *
     * `z.stringbool()`, not `z.coerce.boolean()`: the latter is JavaScript truthiness, under
     * which the *string* `"false"` is `true` — so `?includeUnready=false` would have meant the
     * opposite of what it says.
     */
    includeUnready: z.stringbool().default(false),
});
//# sourceMappingURL=builds.js.map